import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { isEmpty } from 'lodash';

import StatusBadges from '@components/badges/StatusBadges';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import AgentActionButtons from './actionButtons/AgentActionButtons';
import Button from '../smartAdminUI/Button';

const AgentsTable = ({
  displayData,
  onAgentSelect,
  onAuditClick,
  isModal = false,
}) => {
  // NOTE: P9 mentioned that isCopiable will always be false so its not included
  const actionsFormatter = (cell, row) => (
    <AgentActionButtons
      viewClicked={onAgentSelect}
      auditClicked={onAuditClick}
      currRow={row}
      isEditable={row?.isEditable}
      isAuditable={row?.isAuditable}
    />
  );
  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'actions',
      text: '',
      formatter: actionsFormatter,
      headerStyle: { width: '100px' },
    },
    {
      dataField: 'UniqueID',
      text: 'ID',
      hidden: true,
    },
    {
      dataField: 'MemberMlsId',
      text: 'MLS ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'MemberStatus',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '70px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'MemberFirstName',
      text: 'First Name',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'MemberLastName',
      text: 'Last Name',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'OfficeName',
      text: 'Office Name',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'OfficePhone',
      text: 'Office Phone',
    },
  ];

  const sizePerPageRenderer = ({
    options,
    currSizePerPage,
    onSizePerPageChange,
  }) => (
    <div className="btn-group" role="group">
      {
        options.map(option => {
          const isSelect = currSizePerPage === `${option.page}`;
          return (
            <Button
              variant={`${isSelect ? 'primary' : 'outline-primary'}`}
              key={option.text}
              onClick={() => onSizePerPageChange(option.page)}
            >
              {option.text}
            </Button>
          );
        })
      }
    </div>
  );

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      &nbsp;
      {`Showing rows ${from} to ${to} of ${size}`}
    </span>
  );

  const options = {
    firstPageText: '<<',
    hidePageListOnlyOnePage: true,
    lastPageText: '>>',
    nextPageText: '>',
    paginationTotalRenderer: customTotal,
    prePageText: '<',
    showTotal: true,
    sizePerPageList: [],
    sizePerPage: isModal ? 5 : 10,
    sizePerPageRenderer,
  };

  return (
    <ToolkitProvider
      columns={columns}
      data={displayData}
      keyField="UniqueID"
      search
    >
      {toolKit => (
        <>
          {displayData.length > 1 && <TableSearchInputGroup {...toolKit.searchProps} />}
          <BootstrapTable
            defaultSorted={[{ dataField: 'OfficeName', order: 'desc' }]}
            wrapperClasses="table-responsive"
            classes="dataTable"
            bordered={false}
            striped
            hover
            id="agentsTable"
            noDataIndication="Your search returned no results. Please try adjusting your search criteria."
            pagination={displayData.length > 1 ? paginationFactory(options) : null}
            {...toolKit.baseProps}
          />
        </>
      )}
    </ToolkitProvider>
  );
};

AgentsTable.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onAgentSelect: PropTypes.func.isRequired,
  onAuditClick: PropTypes.func.isRequired,
  isModal: PropTypes.bool,
};

export default AgentsTable;
